import { z } from 'zod';
import superjson from 'superjson';

export const schema = z.object({
  sessionId: z.string().min(1),
  messageContent: z.string().min(1),
  extractedFiles: z
    .array(
      z.object({
        filename: z.string(),
        code: z.string(),
      }),
    )
    .optional(),
});

export type InputType = z.infer<typeof schema>;

export interface AiRecommendation {
  id: string;
  title: string;
  description: string;
  priority: 'high' | 'medium' | 'low';
}

export interface RecommendationsOutput {
  proTip: string;
  recommendations: AiRecommendation[];
}

// Cooldown + in-flight guard to prevent parallel request spam
let recsCooldownUntil = 0;
let recsConsecutiveFailures = 0;
let recsInFlight: Promise<RecommendationsOutput | null> | null = null;
const RECS_COOLDOWN_MS = 30_000;
const RECS_MAX_COOLDOWN_MS = 300_000;

export const fetchAiRecommendations = async (
  body: InputType,
  init?: RequestInit,
): Promise<RecommendationsOutput | null> => {
  if (Date.now() < recsCooldownUntil) return null;
  if (recsInFlight) return null;

  const doFetch = async (): Promise<RecommendationsOutput | null> => {
    try {
      const validatedInput = schema.parse(body);
      const result = await fetch(`/_api/ai/recommendations`, {
        method: 'POST',
        credentials: 'include',
        body: superjson.stringify(validatedInput),
        ...init,
        headers: {
          'Content-Type': 'application/json',
          ...(init?.headers ?? {}),
        },
      });
      if (!result.ok) {
        recsConsecutiveFailures++;
        const backoff = Math.min(
          RECS_COOLDOWN_MS * Math.pow(2, recsConsecutiveFailures - 1),
          RECS_MAX_COOLDOWN_MS,
        );
        recsCooldownUntil = Date.now() + backoff;
        return null;
      }
      recsConsecutiveFailures = 0;
      return superjson.parse<RecommendationsOutput>(await result.text());
    } catch {
      recsConsecutiveFailures++;
      const backoff = Math.min(
        RECS_COOLDOWN_MS * Math.pow(2, recsConsecutiveFailures - 1),
        RECS_MAX_COOLDOWN_MS,
      );
      recsCooldownUntil = Date.now() + backoff;
      return null;
    }
  };

  recsInFlight = doFetch();
  try {
    return await recsInFlight;
  } finally {
    recsInFlight = null;
  }
};
